import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  NgZone,
  Signal,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { SharedModule } from '@shared/shared.module';
import { ESplashLoaderComponent } from '@shared/components/loaders/e-splash-loader/e-splash-loader.component';
import { loadHdChartGlobalSettings, saveHdChartGlobalSettings } from '../../chart/bodygraph/chart-tabs/hd-chart-global-settings';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../../chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../../chart/bodygraph/hd-gate-info.service';
import { HdPlanetInfoService } from '../../chart/bodygraph/hd-planet-info.service';
import {
  HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON,
  HD_PLANET_ORDER_STANDARD,
  HdPlanetKey,
  getHdPlanetDisplayConfig,
} from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdTransitShiftPopupService } from '../shift-popup/hd-transit-shift-popup.service';
import { HdTransitShiftPopupData } from '../shift-popup/hd-transit-shift-popup.models';
import { HdTransitLegendFilterComponent } from '../legend-filter/hd-transit-legend-filter.component';
import { HdTransitMomentAddRequest } from '../transit-moment-add.models';
import { legendCategoryOfKey } from '../transit-legend.utils';
import { TransitDataService } from '../transit-data.service';
import { TransitViewTimeService } from '../transit-view-time.service';
import { HdTransitsLanesScrollState, HdTransitsViewStateService } from '../transits-view-state.service';
import { HdTransitPlanetSegments } from '../transit.models';
import {
  HdEphemerisGateEpoch,
  HdEphemerisLaneVm,
  HdEphemerisRange,
  HdEphemerisRenderItem,
  HdEphemerisRetroLoopSpan,
  HdEphemerisRulerTick,
  HdEphemerisStationVm,
  HdEphemerisYearBox,
  HdEphemerisYearBoxVm,
  HdEphemerisYearDatesTier,
  HdEphemerisZoomConfig,
  HdEphemerisZoomLevel,
} from './hd-ephemeris-lanes.models';
import {
  HD_EPHEMERIS_BOUNDARY_HIT_WIDTH_PX,
  HD_EPHEMERIS_DEFAULT_ZOOM,
  HD_EPHEMERIS_DOMAIN_MAX_MS,
  HD_EPHEMERIS_DOMAIN_MIN_MS,
  HD_EPHEMERIS_GATE_HEIGHT_PX,
  HD_EPHEMERIS_GATE_Y_PX,
  HD_EPHEMERIS_LANE_HEIGHT,
  HD_EPHEMERIS_RULER_HEIGHT_PX,
  HD_EPHEMERIS_WEEK_LINE_LABEL_MIN_PX,
  HD_EPHEMERIS_YEAR_DATES_Y_PX,
  HD_EPHEMERIS_YEAR_LANE_HEIGHT,
  HD_EPHEMERIS_YEAR_TITLE_Y_PX,
  HD_EPHEMERIS_YEAR_ZOOM,
  HD_EPHEMERIS_ZOOM_LEVELS,
  adaptLabels,
  buildGateEpochs,
  clampRangeToDomain,
  clipSegmentsToWindow,
  computeBaseRange,
  computeBlockBoundaries,
  computeRulerTicks,
  computeVisibleWindow,
  detectRetroLoops,
  expandRange,
  flattenGateBlocks,
  hexToRgba,
  mergeTinyBlocks,
  projectGateEpochs,
  projectSegmentsToBlocks,
  projectRetrogradeSpans,
  projectStations,
  pxToTime,
  rangeCovers,
  retroLoopPath,
  timeToPx,
  unionRanges,
  yearZoomAvailable,
  zoomLevelConfig,
} from './hd-ephemeris-lanes.utils';

const I18N_PREFIX = 'humanDesign.chart.transits.lanes';
/** Debounce op data-aanvragen bij snelle zoom/scroll-acties (request-storm-remmer). */
const LOAD_DEBOUNCE_MS = 150;
/** Viewport-breedte vóór de eerste ResizeObserver-meting (tests/SSR). */
const FALLBACK_VIEWPORT_WIDTH_PX = 960;
/** Scroll-stap van de pijltjestoetsen (besluit 19). */
const KEY_SCROLL_PX = 240;
/** Sleepafstand (px) waarboven een pointer-drag als pan telt i.p.v. als klik. */
const DRAG_PAN_THRESHOLD_PX = 4;
/**
 * Overscan-marge (× vensterbreedte) van de cliprange waar de blok-pipeline op
 * klipt: de lanes herberekenen pas als het zichtbare venster meer dan deze
 * marge verschoven is, niet elk scroll-frame. De rulers volgen wél vloeiend.
 */
const LANES_CLIP_OVERSCAN_FACTOR = 0.5;
/** Lokale datum/tijd-weergave in aria-teksten, bv. "25 jul 06:00" (spec §4a). */
const BLOCK_TIME_FORMAT = 'D MMM HH:mm';
/** Jaren-weergave: het selectielabel heeft het jaartal nodig (decennia in beeld). */
const YEAR_SELECTION_FORMAT = 'D MMM YYYY';
/** Datums in een poortperiode-blok per labelvariant (lokale tijd, Belgische notatie). */
const YEAR_BOX_DATE_FORMATS: Readonly<Record<NonNullable<HdEphemerisYearDatesTier>, string>> = {
  full: 'DD/MM/YYYY',
  short: 'DD/MM/YY',
  years: 'YYYY',
};
/** Een poortperiode waarvan de eerste intrede of laatste uittrede (nog) buiten de geladen data valt. */
const YEAR_BOX_UNKNOWN_DATE = '…';
/** Volledige lokale datum/tijd in de hover-tooltip op een blok-overgang. */
const BOUNDARY_TIME_FORMAT = 'ddd D MMM YYYY HH:mm';
const GATE_FILL_ALPHA = 0.12;
const LINE_FILL_ALPHA = 0.22;
/** Jaren-weergave: lichte tint over de hele poortperiode, sterkere tint op de doorgangen. */
const YEAR_AWAY_FILL_ALPHA = 0.07;
const YEAR_VISIT_FILL_ALPHA = 0.3;

/** Alle 14 planeten — Chiron zit in de data; de UI filtert via de showChiron-setting (besluit 9). */
const ALL_PLANET_KEYS: readonly HdPlanetKey[] = HD_PLANET_DISPLAY_CONFIGS_WITH_CHIRON.map(config => config.key);

/**
 * Lane-volgorde: de standaard planeetvolgorde (`HD_PLANET_ORDER_STANDARD` —
 * maan onder de nodes; zelfde default als het Nu & Straks-panel) zodat de
 * kleur-categorieën bij elkaar blijven; Chiron staat als extensie achteraan
 * (besluit 4/9).
 */
const LANE_ORDER: readonly HdPlanetKey[] = [...HD_PLANET_ORDER_STANDARD, 'chiron'];

/**
 * Initieel zoom-niveau uit de gebruikerssettings; een geparkeerd/onbekend
 * niveau (bv. 'month') valt terug op de default.
 */
function initialZoomFromSettings(): HdEphemerisZoomLevel {
  const saved = loadHdChartGlobalSettings().transitsLanesZoom;
  return HD_EPHEMERIS_ZOOM_LEVELS.includes(saved) ? saved : HD_EPHEMERIS_DEFAULT_ZOOM;
}

/** Stalen voor de jaren-legende: periode = doorgang · buurpoort · doorgang, plus de twee tinten apart. */
function yearLegendColorsFor(color: string): { stroke: string; period: string; visit: string; away: string } {
  const visit = hexToRgba(color, YEAR_VISIT_FILL_ALPHA);
  const away = hexToRgba(color, YEAR_AWAY_FILL_ALPHA);
  return {
    stroke: color,
    period: `linear-gradient(to right, ${visit} 0 35%, ${away} 35% 62%, ${visit} 62%)`,
    visit,
    away,
  };
}

/**
 * Ephemeris-lanes (spec §4, visualisatie-doc §1 — de kern-tijdlijnview van de
 * transits-feature): per planeet een lane met poort-containers en lijn-items
 * op een gedeelde tijd-as, een live meelopende nu-lijn, retro-arcering + ℞ +
 * lus-verbindingen, stations als S-markers, en vier zoom-niveaus.
 *
 * - **Coördinaten**: één SVG over de geladen range (oorsprong =
 *   `loadedRange.fromMs`, lineair via `zoomConfig.pxPerMs`); de scroll-container
 *   clipt visueel, de pure pipeline clipt/cult de blokken op het zichtbare
 *   venster (budget ~2000 blokken, besluit 20). Geen virtual scroll.
 * - **Data**: een debounced effect laadt `baseRange` (3× venster rond het
 *   anker) en verlengt de horizon zodra het zichtbare venster de marge nadert;
 *   de chunk-cache van de service maakt verlengen goedkoop. Links bijladen
 *   compenseert de scroll zodat de kijktijd stabiel blijft.
 * - **Domein**: begrensd op 1800–2399; aan de rand verschijnt
 *   `rangeLimitReached` i.p.v. een crash.
 * - **Ruler**: twee datum/tijd-stroken (header boven, footer onder de lanes)
 *   op de gedeelde x-schaal — sticky in de verticale scroll-context van de
 *   panel-wrapper, horizontaal gevoed door dezelfde `visibleWindow` als de
 *   lanes. Eigen laag (`layer-ruler`): hier komen later events/markers op.
 * - De nu-lijn is de enige computed die op de 1s-`viewTime`-tick herberekent;
 *   de blok-VM's hangen van venster/data/taal af, niet van de klok.
 */
@Component({
  selector: 'hd-ephemeris-lanes',
  imports: [SharedModule, ESplashLoaderComponent, HdTransitLegendFilterComponent],
  templateUrl: './hd-ephemeris-lanes.component.html',
  styleUrl: './hd-ephemeris-lanes.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdEphemerisLanesComponent {
  readonly showChiron = input<boolean>(loadHdChartGlobalSettings().showChiron);
  readonly initialZoom = input<HdEphemerisZoomLevel>(initialZoomFromSettings());
  readonly zoomChanged = output<HdEphemerisZoomLevel>();
  /** Plus-knop op de selectielijn: het transits-paneel opent de momentdialoog. */
  readonly momentAddRequested = output<HdTransitMomentAddRequest>();

  private readonly dataService = inject(TransitDataService);
  private readonly viewTimeService = inject(TransitViewTimeService);
  private readonly popupService = inject(HdTransitShiftPopupService);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfo = inject(HdPlanetInfoService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly viewState = inject(HdTransitsViewStateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);
  /** Host-element = de verticale scroll-wrapper van het panel (`.transits-panel__lanes`). */
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  protected readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollEl');
  protected readonly planetsColumn = viewChild<ElementRef<HTMLElement>>('planetsEl');

  protected readonly zoomLevels = HD_EPHEMERIS_ZOOM_LEVELS;
  protected readonly yearZoom = HD_EPHEMERIS_YEAR_ZOOM;
  protected readonly yearTitleYPx = HD_EPHEMERIS_YEAR_TITLE_Y_PX;
  protected readonly yearDatesYPx = HD_EPHEMERIS_YEAR_DATES_Y_PX;
  /** Legende-stalen van de jaren-weergave in de transpersoonlijke kleur (de enige lanes daar). */
  protected readonly yearLegendColors = yearLegendColorsFor(getHdPlanetDisplayConfig('pluto').stripeColor);
  /** Verticale span van de hover-zone op een blok-overgang (de hele poort-band). */
  protected readonly boundaryYPx = HD_EPHEMERIS_GATE_Y_PX;
  protected readonly boundaryHeightPx = HD_EPHEMERIS_GATE_HEIGHT_PX;
  protected readonly boundaryHitWidthPx = HD_EPHEMERIS_BOUNDARY_HIT_WIDTH_PX;
  protected readonly rulerHeight = HD_EPHEMERIS_RULER_HEIGHT_PX;
  /** Nu-markering op de ruler: driehoekje dat naar de lanes wijst (boven-/onderstrook gespiegeld). */
  protected readonly rulerNowMarkerTop = 'M -3 0 L 3 0 L 0 5 Z';
  protected readonly rulerNowMarkerBottom = 'M -3 5 L 3 5 L 0 0 Z';
  protected readonly zoomLabelKeys: Readonly<Record<HdEphemerisZoomLevel, string>> = {
    hour: 'zoomHour',
    day: 'zoomDay',
    week: 'zoomWeek',
    month: 'zoomMonth',
    year: 'zoomYear',
  };

  /** Actief zoom-niveau; volgt de `initialZoom`-input tot de gebruiker zelf zoomt. */
  readonly zoom = linkedSignal(() => this.initialZoom());

  /** Kijk-tijd (ms) waar het venster omheen gebouwd is — initieel "nu", na zoom het zichtbare midden. */
  private readonly anchorMs = signal(0);
  private readonly scrollLeftPx = signal(0);
  protected readonly viewportWidthPx = signal(FALLBACK_VIEWPORT_WIDTH_PX);
  /** Breedte van de sticky planeetkolom — de ruler-spacer lijnt de ticks hiermee uit. */
  protected readonly planetsWidthPx = signal(0);
  private readonly loadedRange = signal<HdEphemerisRange | null>(null);
  private readonly planetSegments = signal<readonly HdTransitPlanetSegments[]>([]);
  protected readonly hoveredGateId = signal<string | null>(null);
  /** true zodra een pointer-drag de pan-drempel passeert (grabbing-cursor + tekstselectie uit). */
  protected readonly panning = signal(false);
  /**
   * Zet op de pointerup die een pan afsluit; de click die daar direct op volgt
   * mag geen blok-popup openen. Los van {@link panning} zodat de grabbing-
   * cursor meteen bij het loslaten verdwijnt i.p.v. tot de volgende pointerdown.
   */
  private panEndedClickPending = false;

  /** Load-overlay alleen bij eerste load of zoom-wissel (achtergrond-verlenging toont geen overlay). */
  protected readonly loading = computed(() => this.dataService.segmentsLoading() && this.planetSegments().length === 0);
  protected readonly error: Signal<string | null> = this.dataService.segmentsError;
  protected readonly fallbackNotice: Signal<string | null> = this.dataService.granularityFallbackNotice;

  private readonly lang = computed(() => {
    // Meelezen zodat de eerste translate ná een async translation-load opnieuw
    // evalueert (zie HdContentLanguageService.translationsLoadedTick).
    this.contentLang.translationsLoadedTick();
    return this.contentLang.effectiveLang();
  }, HD_LANG_RECOMPUTE_ON_LOAD);

  private readonly planetKeys = computed<readonly HdPlanetKey[]>(() =>
    this.showChiron() ? ALL_PLANET_KEYS : ALL_PLANET_KEYS.filter(key => key !== 'chiron'),
  );

  /** Zichtbare lanes: planetKeys minus de uitgevinkte categorieën van de filter-legende. */
  private readonly visiblePlanetKeys = computed<readonly HdPlanetKey[]>(() => {
    const enabled = this.viewState.enabledLegendCategories();
    return this.planetKeys().filter(key => enabled.has(legendCategoryOfKey(key)));
  });

  protected readonly zoomConfig = computed(() => zoomLevelConfig(this.zoom()));

  /**
   * De jaren-weergave (poortwissels van Uranus, Neptunus en Pluto over
   * decennia) kan enkel zolang alle zichtbare lanes transpersoonlijk zijn;
   * daarbuiten staat de knop uit met een tooltip.
   */
  protected readonly yearsAvailable = computed(() => yearZoomAvailable(this.visiblePlanetKeys()));
  protected readonly isYearView = computed(() => this.zoom() === HD_EPHEMERIS_YEAR_ZOOM);
  protected readonly laneHeightPx = computed(() =>
    this.isYearView() ? HD_EPHEMERIS_YEAR_LANE_HEIGHT : HD_EPHEMERIS_LANE_HEIGHT,
  );

  protected readonly yearZoomTooltip = computed(() =>
    this.t(this.yearsAvailable() ? 'zoomYearTooltip' : 'zoomYearUnavailableTooltip'),
  );

  /**
   * Op te halen planeten: op de gewone niveaus alle planeten (een filterwissel
   * hoeft dan niet te herladen); in de jaren-weergave enkel de zichtbare
   * transpersoonlijke, want tientallen jaren poortdata van bv. de maan is zwaar
   * en wordt er nooit getoond.
   */
  private readonly loadPlanetKeys = computed<readonly HdPlanetKey[]>(() =>
    this.isYearView()
      ? this.visiblePlanetKeys().filter(key => legendCategoryOfKey(key) === 'outer-trans')
      : this.planetKeys(),
  );

  /** Initieel geladen bereik rond het anker, begrensd op het domein (zie `computeBaseRange`). */
  private readonly baseRange = computed<HdEphemerisRange>(() =>
    computeBaseRange(this.anchorMs(), this.viewportWidthPx(), this.zoomConfig()),
  );

  /** De range waar px-coördinaten op rusten: geladen zodra beschikbaar, anders de basis. */
  private readonly effectiveRange = computed<HdEphemerisRange>(() => this.loadedRange() ?? this.baseRange());

  protected readonly visibleWindow = computed<HdEphemerisRange>(() =>
    computeVisibleWindow(this.scrollLeftPx(), this.viewportWidthPx(), this.effectiveRange().fromMs, this.zoomConfig()),
  );

  /** Zichtbaar venster + laadmarge, begrensd op het domein — de laad-trigger van het effect. */
  private readonly requiredRange = computed<HdEphemerisRange>(() =>
    clampRangeToDomain(expandRange(this.visibleWindow(), this.zoomConfig().loadMarginMs)),
  );

  protected readonly svgWidth = computed(() => {
    const range = this.effectiveRange();
    return Math.max(1, (range.toMs - range.fromMs) * this.zoomConfig().pxPerMs);
  });
  protected readonly svgHeight = computed(() => this.lanes().length * this.laneHeightPx());

  /**
   * Retro-lussen per planeet — de detectie is O(n²) over de segmenten en hangt
   * enkel van de data af, dus staat hij buiten de per-scroll-frame `lanes()`.
   */
  private readonly retroLoopsByPlanet = computed(() => {
    const byKey = new Map<HdPlanetKey, HdEphemerisRetroLoopSpan[]>();
    for (const planet of this.planetSegments()) {
      if (planet.planetKey != null) byKey.set(planet.planetKey, detectRetroLoops(planet.segments));
    }
    return byKey;
  });

  /**
   * Jaren-weergave: de keynotes van een poort ("keynote 1 · keynote 2") voor de
   * tooltip van een poortperiode, gecachet per taal/scope-load: `gateInfoIn`
   * leest de vertaalscope, dus niet bij elke herberekening van de lanes opnieuw.
   */
  private readonly gateKeynotes = computed(() => {
    const lang = this.lang();
    const cache = new Map<number, string>();
    return (gate: number): string => {
      const cached = cache.get(gate);
      if (cached != null) return cached;
      const translations = this.gateInfo.gateInfoIn(gate, lang).translations;
      const keynotes = [translations.keynote1, translations.keynote2].filter(keynote => keynote.length > 0).join(' · ');
      cache.set(gate, keynotes);
      return keynotes;
    };
  });

  /** Jaren-weergave: de poortperiodes per planeet — hangen enkel van de data af, niet van de scroll. */
  private readonly gateEpochsByPlanet = computed(() => {
    const byKey = new Map<HdPlanetKey, HdEphemerisGateEpoch[]>();
    if (!this.isYearView()) return byKey;
    for (const planet of this.planetSegments()) {
      if (planet.planetKey != null) byKey.set(planet.planetKey, buildGateEpochs(planet.segments));
    }
    return byKey;
  });

  /** Lanes + blokken — herberekend op cliprange-verschuiving/data/taal, níét op elk scroll-frame. */
  protected readonly lanes = computed<readonly HdEphemerisLaneVm[]>(() => {
    const lang = this.lang();
    const window = this.visibleWindow();
    const config = this.zoomConfig();
    const isWeek = this.zoom() === 'week';
    const originMs = this.effectiveRange().fromMs;
    // De blok-pipeline klipt op de overscan-cliprange (scroll-perf): de rulers
    // volgen het live venster, de blokken herberekenen pas bij > marge-scroll.
    this.lanesClipVersion();
    const clipRange = this.lanesClipRange ?? window;
    const visibleKeys = this.visiblePlanetKeys();
    if (this.isYearView()) {
      const epochsByPlanet = this.gateEpochsByPlanet();
      return LANE_ORDER.filter(key => visibleKeys.includes(key)).map((key, index) =>
        this.yearLane(key, index, lang, projectGateEpochs(epochsByPlanet.get(key) ?? [], clipRange, originMs, config)),
      );
    }
    const dataByKey = new Map<HdPlanetKey, HdTransitPlanetSegments>();
    for (const planet of this.planetSegments()) {
      if (planet.planetKey != null) dataByKey.set(planet.planetKey, planet);
    }
    const loopsByPlanet = this.retroLoopsByPlanet();
    return LANE_ORDER.filter(key => visibleKeys.includes(key)).map((key, index) => {
      const planetConfig = getHdPlanetDisplayConfig(key);
      const name = this.planetInfo.planetInfoIn(planetConfig.key, lang).name;
      const segments = dataByKey.get(planetConfig.key)?.segments ?? [];
      const retrogradeSpans = dataByKey.get(planetConfig.key)?.retrogradeSpans ?? [];
      // Maan in week-view: te snel voor lijnblokjes én voor horizontale
      // poortlabels — enkel poortblokjes met een verticaal poortnummer, en
      // nooit samengevoegd tot "…"-blokken (elke poort blijft zichtbaar).
      const verticalGateLabels = isWeek && planetConfig.key === 'moon';
      const blocks = projectSegmentsToBlocks(
        clipSegmentsToWindow(segments, clipRange),
        originMs,
        config,
        retrogradeSpans,
      ).map(block => ({
        ...block,
        // Week: nooit het volle `poort.lijn`-label op lijnblokjes — enkel
        // `.lijn`, al vanaf de minimale tekstbreedte, en niet samengevoegd
        // (elk lijnblokje blijft zichtbaar met zijn nummer).
        lines: verticalGateLabels
          ? []
          : isWeek
            ? adaptLabels(block.lines, Number.POSITIVE_INFINITY, HD_EPHEMERIS_WEEK_LINE_LABEL_MIN_PX)
            : mergeTinyBlocks(adaptLabels(block.lines)),
      }));
      const gateBlocks = adaptLabels(blocks, undefined, verticalGateLabels ? 0 : undefined);
      // Arcering exact van station tot station, ook midden in een blok.
      const retrogradePx = projectRetrogradeSpans(retrogradeSpans, clipRange, originMs, config);
      const items = flattenGateBlocks(
        verticalGateLabels ? gateBlocks : mergeTinyBlocks(gateBlocks),
        retrogradePx,
      ).map(item => ({
        ...item,
        // Verticale poortlabels staan gecentreerd in het blok i.p.v. links bovenaan.
        ...(verticalGateLabels && item.kind === 'gate'
          ? {
              labelXPx: item.xPx + item.widthPx / 2,
              labelYPx: HD_EPHEMERIS_GATE_Y_PX + HD_EPHEMERIS_GATE_HEIGHT_PX / 2,
              labelAnchor: 'middle' as const,
            }
          : {}),
        label: item.kind === 'merged' ? { prefix: '', strong: this.t('mergedBlocksLabel') } : item.label,
        ariaLabel:
          item.kind === 'merged'
            ? this.t('mergedBlocksAria', { count: item.count })
            : this.blockAriaLabel(name, item),
      }));
      const stations: HdEphemerisStationVm[] = projectStations(
        dataByKey.get(planetConfig.key)?.stations ?? [],
        clipRange,
        originMs,
        config,
      ).map(station => ({
        ...station,
        ariaLabel: this.t(station.turnsTo === 'direct' ? 'turnsDirectAria' : 'turnsRetrogradeAria', {
          planet: name,
          time: station.atUtc.clone().local().format(BLOCK_TIME_FORMAT),
        }),
      }));
      const loops = (loopsByPlanet.get(planetConfig.key) ?? [])
        .filter(loop => loop.toMs >= clipRange.fromMs && loop.fromMs <= clipRange.toMs)
        .map(loop => {
          const x1Px = timeToPx(loop.fromMs, originMs, config);
          const x2Px = timeToPx(loop.toMs, originMs, config);
          return {
            id: `loop-${loop.gate}-${loop.fromMs}`,
            gate: loop.gate,
            x1Px,
            x2Px,
            pathD: retroLoopPath(x1Px, x2Px),
            ariaLabel: this.t('retrogradeLoopAria', { planet: name, gate: loop.gate }),
          };
        });
      const boundaries = computeBlockBoundaries(items).map(boundary => ({
        ...boundary,
        tooltip: boundary.atUtc.clone().local().format(BOUNDARY_TIME_FORMAT),
      }));
      return {
        planetKey: planetConfig.key,
        planetId: planetConfig.planetId,
        name,
        glyph: planetConfig.astronomiconChar,
        stripeColor: planetConfig.stripeColor,
        gateFill: hexToRgba(planetConfig.stripeColor, GATE_FILL_ALPHA),
        lineFill: hexToRgba(planetConfig.stripeColor, LINE_FILL_ALPHA),
        yPx: index * HD_EPHEMERIS_LANE_HEIGHT,
        items,
        stations,
        loops,
        boundaries,
        verticalGateLabels,
        yearBoxes: [],
        yearAwayFill: '',
        yearVisitFill: '',
      };
    });
  });

  /** X van de nu-lijn; `null` als "nu" buiten de geladen range valt. */
  protected readonly nowX = computed<number | null>(() => {
    const x = timeToPx(this.viewTimeService.viewTime().valueOf(), this.effectiveRange().fromMs, this.zoomConfig());
    return x >= 0 && x <= this.svgWidth() ? x : null;
  });

  /**
   * Ruler-ticks op het zichtbare venster (adaptief per zoom-niveau). De ruler
   * rendert in viewport-coördinaten: x relatief aan `window.fromMs`.
   */
  protected readonly rulerTicks = computed(() => {
    // Dag-/maandnamen (globale moment-locale) wisselen mee met de taal.
    this.lang();
    return computeRulerTicks(this.visibleWindow(), this.zoom(), this.zoomConfig().pxPerMs);
  });

  /**
   * Streepjeslengte per tick-soort: major (dag-/maandwissel) het langst,
   * gelabelde ticks halflang, ongelabelde tussenstreepjes het kortst.
   */
  protected rulerTickLength(tick: HdEphemerisRulerTick): number {
    if (tick.major) return 12;
    return tick.label != null ? 6 : 3;
  }

  /** Nu-positie in ruler-coördinaten — de nu-lijn loopt over de ruler-stroken door. */
  protected readonly rulerNowX = computed<number | null>(() => {
    const x = this.nowX();
    return x == null ? null : x - this.scrollLeftPx();
  });

  protected readonly nowLineAria = computed(() =>
    this.t('nowLineAria', { time: this.viewTimeService.viewTime().clone().local().format(BLOCK_TIME_FORMAT) }),
  );

  /**
   * Geselecteerd tijdstip (klik op de tijdlijn) — de blauwe selectielijn in
   * het blauw van de kanaal-highlights. Component-niveau: overleeft een
   * zoom-wissel; de lijn rendert enkel als hij in de geladen range valt.
   */
  protected readonly selectedTimeMs = signal<number | null>(null);

  /** X van de selectielijn; `null` zonder selectie of buiten de geladen range. */
  protected readonly selectedX = computed<number | null>(() => {
    const timeMs = this.selectedTimeMs();
    if (timeMs == null) return null;
    const x = timeToPx(timeMs, this.effectiveRange().fromMs, this.zoomConfig());
    return x >= 0 && x <= this.svgWidth() ? x : null;
  });

  /** Selectie-positie in ruler-coördinaten — de lijn loopt over de ruler-stroken door. */
  protected readonly rulerSelectedX = computed<number | null>(() => {
    const x = this.selectedX();
    return x == null ? null : x - this.scrollLeftPx();
  });

  /** Kort tijdstip-label voor de rulers (bv. "3 okt 14:35"; in de jaren-weergave "3 okt 2005"). */
  protected readonly selectionTimeLabel = computed<string | null>(() => {
    const timeMs = this.selectedTimeMs();
    this.lang();
    return timeMs == null ? null : moment(timeMs).format(this.isYearView() ? YEAR_SELECTION_FORMAT : BLOCK_TIME_FORMAT);
  });

  /** Volledige datum/tijd voor de hover-tooltip op de selectielijn. */
  protected readonly selectionTimeTooltip = computed<string | null>(() => {
    const timeMs = this.selectedTimeMs();
    this.lang();
    return timeMs == null ? null : moment(timeMs).format(BOUNDARY_TIME_FORMAT);
  });

  protected readonly selectionLineAria = computed(() =>
    this.t('selectionLineAria', { time: this.selectionTimeTooltip() ?? '' }),
  );

  protected readonly addEventAria = computed(() =>
    this.t('addEventAria', { time: this.selectionTimeTooltip() ?? '' }),
  );

  /** true aan de rand van de ephemeris-dekking (1800–2399): verder navigeren kan niet. */
  protected readonly rangeLimitReached = computed(() => {
    const window = this.visibleWindow();
    return window.fromMs <= HD_EPHEMERIS_DOMAIN_MIN_MS || window.toMs >= HD_EPHEMERIS_DOMAIN_MAX_MS;
  });

  protected readonly isEmpty = computed(
    () =>
      !this.loading() &&
      this.error() == null &&
      this.loadedRange() != null &&
      this.planetSegments().every(planet => planet.segments.length === 0),
  );

  /**
   * Vaste minimale hoogte van de stage (2× ruler + één lane per planeet). Zo
   * behoudt de view zijn hoogte terwijl een nog-ongecachede zoom-wissel op
   * data wacht — de loader overlayt een al-volle stage, er verspringt niets.
   */
  protected readonly stageMinHeightPx = computed(
    () => 2 * HD_EPHEMERIS_RULER_HEIGHT_PX + this.visiblePlanetKeys().length * this.laneHeightPx(),
  );

  protected readonly showBody = computed(() => !this.loading() && this.error() == null && !this.isEmpty());

  private loadSeq = 0;
  /** Signature (granulariteit + planeten) van de laatst afgeronde load — zie het laad-effect. */
  private loadedSignature: string | null = null;
  /** Na eerste load of zoom-wissel éénmalig de scroll op het anker centreren. */
  private needsRecenter = true;
  /** Bewaarde scroll-staat die bij de eerste load hersteld wordt (na een dock-tab-wissel). */
  private pendingScrollRestore: HdTransitsLanesScrollState | null = null;
  private pendingRestoreFrame: { readonly frameId: number; readonly view: Window } | null = null;
  /** Actieve pan-drag: startpositie van de cursor + de scrollstand bij pointerdown. */
  private panStart: { readonly pointerId: number; readonly clientX: number; readonly scrollLeftPx: number } | null =
    null;
  /** Laatst gemelde DOM-scrollLeft — de trailing edge van de scroll-throttle. */
  private latestDomScrollLeftPx = 0;
  /** Lopend rAF-frame van de scroll-throttle; `null` = geen frame gepland. */
  private scrollFrame: { readonly frameId: number; readonly view: Window } | null = null;
  /** Laatste programmaticaal toegepaste scrollLeft — onderscheidt scrollbar-scrolls van de gebruiker van eigen toepassingen. */
  private lastProgrammaticScrollPx = 0;
  /**
   * true zodra de gebruiker zelf navigeerde (pan/scrollbar/pijltjes) of zijn
   * positie hersteld/gemarkeerd werd — bij een resize behoudt hij dan zijn
   * midden i.p.v. opnieuw op het anker te centreren.
   */
  private userNavigated = false;
  /**
   * Overscan-cliprange voor de blok-pipeline (null = volg het live venster).
   * Schuift enkel op wanneer het zichtbare venster de marge overschrijdt —
   * zie {@link applyScrollPositionPx}.
   */
  private lanesClipRange: HdEphemerisRange | null = null;
  /** Versie-teller die `lanes()` laat herberekenen bij een cliprange-verschuiving. */
  private readonly lanesClipVersion = signal(0);

  constructor() {
    this.anchorMs.set(this.viewTimeService.viewTime().valueOf());

    // Een dock-tab-wissel vernietigt en hermaakt dit component (de dock-host
    // rendert enkel de actieve tab): neem de bewaarde zoom + scrollposities
    // over uit de view-state-service — het paneel-niveau-equivalent van de
    // `tabScrollPositions` van de dock-hosts (die enkel hún eigen
    // content-element en omliggende containers dekken).
    const savedScroll = this.viewState.lanesScrollState();
    if (savedScroll) {
      this.pendingScrollRestore = savedScroll;
      // Een bewaard niveau kan intussen uit de selecteerbare lijst verdwenen
      // zijn (maand): val dan terug op de default i.p.v. een niet-kiesbaar
      // niveau te herstellen. De jaren-weergave mag terugkomen; is ze niet meer
      // beschikbaar (filter gewijzigd), dan zet het terugval-effect hieronder
      // de gewone weergave terug.
      const restorable = savedScroll.zoom === HD_EPHEMERIS_YEAR_ZOOM || this.zoomLevels.includes(savedScroll.zoom);
      this.zoom.set(restorable ? savedScroll.zoom : HD_EPHEMERIS_DEFAULT_ZOOM);
    }

    this.destroyRef.onDestroy(() => {
      const window = this.visibleWindow();
      this.viewState.saveLanesScroll({
        zoom: this.zoom(),
        lanesScrollLeftPx: this.scrollLeftPx(),
        panelScrollTopPx: this.hostElement.nativeElement.scrollTop,
        // Het midden als tijdstip: bij een view-wissel (dock ↔ dialog ↔
        // narrow) komt dit punt weer in het midden, ongeacht de nieuwe breedte.
        centerTimeMs: (window.fromMs + window.toMs) / 2,
      });
      this.cancelPendingScrollRestore();
      if (this.scrollFrame !== null) {
        this.scrollFrame.view.cancelAnimationFrame(this.scrollFrame.frameId);
        this.scrollFrame = null;
      }
    });

    // Data-laad-effect: de chunk-cache maakt her-aanvragen van geladen dagen
    // gratis; de debounce voorkomt een request-storm bij snelle zoom/scroll.
    // De signature (niveau + planeten) forceert een hervraag ook als de
    // range al dekkend is (zoom-wissel, Chiron-toggle). Bijladen gaat tot
    // `prefetchMarginMs` voorbij het beeld: op de gewone niveaus is dat de
    // laadmarge zelf, in de jaren-weergave 15 jaar (dus niet bij elke stap).
    // De jaren-weergave laadt eerst enkel het beeld rond het anker (+1 jaar):
    // vóór de eerste lading hangt `required` aan scroll 0 i.p.v. aan het anker,
    // en zou de eerste (wachtende) lading zo jaren groter worden. De marge van
    // 15 jaar volgt daarna op de achtergrond, zonder laad-overlay.
    effect(onCleanup => {
      const config = this.zoomConfig();
      const required = this.requiredRange();
      const loaded = this.loadedRange();
      const planets = this.loadPlanetKeys();
      const signature = `${config.level}|${config.granularity}|${planets.join(',')}`;
      if (loaded != null && signature === this.loadedSignature && rangeCovers(loaded, required)) return;
      const target = clampRangeToDomain(
        loaded == null
          ? config.baseMarginMs != null
            ? this.baseRange()
            : unionRanges(this.baseRange(), required)
          : unionRanges(loaded, expandRange(this.visibleWindow(), config.prefetchMarginMs)),
      );
      const timer = setTimeout(() => void this.loadSegments(target, config, planets, signature), LOAD_DEBOUNCE_MS);
      onCleanup(() => clearTimeout(timer));
    });

    // Jaren-weergave en meer dan de transpersoonlijke planeten gaan niet samen:
    // vinkt de gebruiker er een andere planeetgroep bij aan, dan springt de
    // view terug naar de gewone weergave (zijn laatst gekozen zoom, rond nu).
    effect(() => {
      if (!this.isYearView() || this.yearsAvailable()) return;
      untracked(() => this.leaveYearView());
    });

    // Filter-wissels (bv. alles uit → één categorie aan) veranderen het
    // aantal lanes en daarmee de svg-hoogte; de browser kan daarbij de
    // horizontale scrollstand klemmen terwijl het scrollLeftPx-signaal
    // bewaard blijft — dezelfde desync als bij de eerste load. Na het
    // render-frame de bewaarde stand opnieuw op de DOM toepassen.
    effect(() => {
      this.visiblePlanetKeys();
      this.afterRenderFrame(() => this.applyLanesScrollLeft(this.scrollLeftPx()));
    });

    // Viewport-breedte volgen (echte breedte zodra gemeten; fallback daarvoor).
    // Bij een breedte-wijziging (shrink/widen, dock-resize, eerste meting in
    // het rechterpaneel) de oriëntatie behouden — zie `onViewportResized`.
    effect(onCleanup => {
      const element = this.scrollContainer()?.nativeElement;
      if (element == null || typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width != null && width > 0) {
          const previousWidth = this.viewportWidthPx();
          const previousWindow = this.visibleWindow();
          this.viewportWidthPx.set(width);
          this.onViewportResized(previousWidth, width, previousWindow);
        }
      });
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });

    // Planeetkolom-breedte volgen: de ruler-spacers boven/onder nemen deze
    // breedte over zodat de ticks exact boven de lanes uitlijnen.
    effect(onCleanup => {
      const element = this.planetsColumn()?.nativeElement;
      if (element == null || typeof ResizeObserver === 'undefined') return;
      const observer = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect.width;
        if (width != null) this.planetsWidthPx.set(width);
      });
      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });

    // Scroll- en wheel-listeners buiten de Angular-zone: in de zone plant élk
    // event (tientallen per seconde bij scrollen) een app-brede change
    // detection; de signal-writes in de handlers markeren de betrokken views
    // zelf al. Scroll passief (de handler preventDefault't nooit); wheel
    // non-passive omdat ctrl+wheel de browser-zoom moet onderdrukken.
    effect(onCleanup => {
      const element = this.scrollContainer()?.nativeElement;
      if (element == null) return;
      const scrollListener = (event: Event): void => this.onScroll(event);
      const wheelListener = (event: WheelEvent): void => this.onWheel(event);
      this.ngZone.runOutsideAngular(() => {
        element.addEventListener('scroll', scrollListener, { passive: true });
        element.addEventListener('wheel', wheelListener);
      });
      onCleanup(() => {
        element.removeEventListener('scroll', scrollListener);
        element.removeEventListener('wheel', wheelListener);
      });
    });
  }

  protected setZoom(level: HdEphemerisZoomLevel): void {
    if (level === this.zoom()) return;
    if (level === HD_EPHEMERIS_YEAR_ZOOM && !this.yearsAvailable()) return;
    // Behoud de kijk-positie: het tijdstip op het ankerpunt van het huidige
    // venster wordt het nieuwe anker — zo blijft de nu-lijn (of de positie van
    // de gebruiker) na de zoom-wissel op dezelfde plek in de tijd.
    const visible = this.visibleWindow();
    this.applyZoom(level, visible.fromMs + (visible.toMs - visible.fromMs) * this.zoomConfig().anchorFraction);
    // De zoom-keuze is een gebruikerssetting (uur/dag/week bij de app-settings).
    // De jaren-weergave niet: die hangt af van de filter, en die is sessie-only.
    if (level !== HD_EPHEMERIS_YEAR_ZOOM) {
      saveHdChartGlobalSettings({ ...loadHdChartGlobalSettings(), transitsLanesZoom: level });
    }
  }

  /** Terug naar de gewone weergave: de laatst bewaarde zoom, rond de kijktijd. */
  private leaveYearView(): void {
    this.pendingScrollRestore = null;
    this.userNavigated = false;
    this.applyZoom(initialZoomFromSettings(), this.viewTimeService.viewTime().valueOf());
  }

  private applyZoom(level: HdEphemerisZoomLevel, anchorMs: number): void {
    this.anchorMs.set(anchorMs);
    this.loadSeq++; // in-flight antwoorden van het vorige niveau gelden niet meer
    this.planetSegments.set([]);
    this.loadedRange.set(null);
    this.lanesClipRange = null; // cliprange is vensterschaal-afhankelijk — vers opbouwen
    this.scrollLeftPx.set(0);
    const element = this.scrollContainer()?.nativeElement;
    if (element != null) element.scrollLeft = 0;
    this.needsRecenter = true;
    this.zoom.set(level);
    this.zoomChanged.emit(level);
  }

  /**
   * Scroll-throttle: de eerste event van een frame reageert meteen (leading
   * edge), events binnen hetzelfde frame worden gecoalesced en aan het einde
   * van het frame éénmalig toegepast (trailing edge). Zo herberekent de UI
   * maximaal één keer per frame, ook bij high-frequency scroll-events.
   */
  protected onScroll(event: Event): void {
    const px = (event.target as HTMLElement).scrollLeft;
    this.latestDomScrollLeftPx = px;
    if (this.scrollFrame != null) return;
    if (px !== this.lastProgrammaticScrollPx) this.userNavigated = true; // scrollbar-scroll van de gebruiker
    this.applyScrollPositionPx(px);
    const view = this.hostElement.nativeElement.ownerDocument.defaultView;
    if (!view) return;
    const frameId = view.requestAnimationFrame(() => {
      this.scrollFrame = null;
      if (this.latestDomScrollLeftPx !== this.scrollLeftPx()) {
        this.applyScrollPositionPx(this.latestDomScrollLeftPx);
      }
    });
    this.scrollFrame = { frameId, view };
  }

  /**
   * Zet de scroll-positie én verschuift de overscan-cliprange van de
   * blok-pipeline enkel wanneer het venster de marge overschrijdt — de
   * rulers/labels blijven vloeiend meelopen, de (dure) lanes-pipeline
   * herberekent niet elk scroll-frame.
   */
  private applyScrollPositionPx(px: number): void {
    this.scrollLeftPx.set(px);
    const live = computeVisibleWindow(px, this.viewportWidthPx(), this.effectiveRange().fromMs, this.zoomConfig());
    const margin = (live.toMs - live.fromMs) * LANES_CLIP_OVERSCAN_FACTOR;
    if (
      this.lanesClipRange == null ||
      live.fromMs < this.lanesClipRange.fromMs ||
      live.toMs > this.lanesClipRange.toMs
    ) {
      this.lanesClipRange = { fromMs: live.fromMs - margin, toMs: live.toMs + margin };
      this.lanesClipVersion.update(version => version + 1);
    }
  }

  /**
   * Scrollt zodat `timeMs` op het ankerpunt van de viewport staat (legende-klik
   * op Nu/Selectie; golden ratio, in de jaren-weergave het midden). Buiten de
   * geladen range: her-ankeren — de laad-verlenging centreert daarna alsnog,
   * zonder de view te flashen.
   */
  protected scrollToMarker(timeMs: number): void {
    this.userNavigated = true;
    const range = this.loadedRange();
    if (range == null || timeMs < range.fromMs || timeMs > range.toMs) {
      this.anchorMs.set(timeMs);
      this.needsRecenter = true;
      return;
    }
    const x =
      timeToPx(timeMs, this.effectiveRange().fromMs, this.zoomConfig()) -
      this.viewportWidthPx() * this.zoomConfig().anchorFraction;
    this.applyLanesScrollLeft(Math.max(0, x));
  }

  /** Legende-klik op "Nu": scroll naar de nu-lijn. */
  protected scrollToNow(): void {
    this.scrollToMarker(this.viewTimeService.viewTime().valueOf());
  }

  /** Legende-klik op "Selectie": scroll naar de selectielijn (als die er is). */
  protected scrollToSelection(): void {
    const timeMs = this.selectedTimeMs();
    if (timeMs != null) this.scrollToMarker(timeMs);
  }

  /** Enter/Space op een klikbaar legende-item doet hetzelfde als de klik. */
  protected onLegendItemKeydown(event: KeyboardEvent, target: 'now' | 'selection'): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    if (target === 'now') this.scrollToNow();
    else this.scrollToSelection();
  }

  /**
   * Oriëntatiebehoud bij breedte-wijzigingen (shrink/widen van de zwevende
   * view, dock-resize, eerste echte meting in het rechterpaneel): wie zelf
   * genavigeerd heeft behoudt zijn midden; anders her-ankeren op het
   * golden-ratio-punt met de echte breedte.
   */
  private onViewportResized(previousWidth: number, nextWidth: number, previousWindow: HdEphemerisRange): void {
    if (Math.abs(nextWidth - previousWidth) < 2) return;
    if (this.loadedRange() == null || this.pendingScrollRestore != null) return; // de load/restore positioneert zelf
    if (this.userNavigated) {
      const centerMs = (previousWindow.fromMs + previousWindow.toMs) / 2;
      const x = timeToPx(centerMs, this.effectiveRange().fromMs, this.zoomConfig()) - nextWidth / 2;
      this.applyLanesScrollLeft(Math.max(0, x));
    } else {
      this.centerScrollOnAnchor();
    }
  }

  /** Pijltjestoetsen scrollen horizontaal (besluit 19; focus staat op de scroll-container). */
  protected onScrollKeydown(event: KeyboardEvent): void {
    const element = this.scrollContainer()?.nativeElement;
    if (element == null) return;
    if (event.key === 'ArrowLeft') {
      this.userNavigated = true;
      element.scrollBy({ left: -KEY_SCROLL_PX });
      event.preventDefault();
    } else if (event.key === 'ArrowRight') {
      this.userNavigated = true;
      element.scrollBy({ left: KEY_SCROLL_PX });
      event.preventDefault();
    }
  }

  /**
   * Slepen met de muis pant de lanes horizontaal — de kleine scrollbar is voor
   * lange bereiken onwerkbaar. Touch/pen laten we met rust: die scrollen al
   * native. Pas voorbij {@link DRAG_PAN_THRESHOLD_PX} telt het als pan; tot dan
   * blijft het een gewone klik op een blok.
   */
  protected onPanPointerDown(event: PointerEvent): void {
    this.panning.set(false);
    this.panEndedClickPending = false;
    this.panStart = null;
    this.removePanMoveListener();
    if (event.button !== 0 || event.pointerType !== 'mouse') return;
    const element = this.scrollContainer()?.nativeElement;
    if (element == null) return;
    this.userNavigated = true;
    this.panStart = { pointerId: event.pointerId, clientX: event.clientX, scrollLeftPx: element.scrollLeft };
    // pointermove alleen tijdens een (mogelijke) drag, en buiten de zone —
    // permanent gebonden zou elke muisbeweging over de lanes een app-brede
    // change detection kosten.
    const moveListener = (moveEvent: PointerEvent): void => this.onPanPointerMove(moveEvent);
    this.ngZone.runOutsideAngular(() => element.addEventListener('pointermove', moveListener));
    this.panMoveCleanup = () => element.removeEventListener('pointermove', moveListener);
  }

  private panMoveCleanup: (() => void) | null = null;

  private removePanMoveListener(): void {
    this.panMoveCleanup?.();
    this.panMoveCleanup = null;
  }

  protected onPanPointerMove(event: PointerEvent): void {
    const start = this.panStart;
    if (start == null || event.pointerId !== start.pointerId) return;
    const element = this.scrollContainer()?.nativeElement;
    if (element == null) return;
    const deltaX = event.clientX - start.clientX;
    if (!this.panning()) {
      if (Math.abs(deltaX) < DRAG_PAN_THRESHOLD_PX) return;
      this.panning.set(true);
      element.setPointerCapture(start.pointerId);
    }
    event.preventDefault(); // geen tekst-/SVG-selectie tijdens het slepen
    element.scrollLeft = Math.max(0, start.scrollLeftPx - deltaX);
    this.applyScrollPositionPx(element.scrollLeft);
  }

  protected onPanPointerUp(event: PointerEvent): void {
    const start = this.panStart;
    if (start == null || event.pointerId !== start.pointerId) return;
    this.removePanMoveListener();
    const element = this.scrollContainer()?.nativeElement;
    if (element?.hasPointerCapture(start.pointerId)) element.releasePointerCapture(start.pointerId);
    this.panStart = null;
    // Sleep-cursor meteen loslaten; enkel de click die op deze pointerup volgt
    // wordt nog onderdrukt zodat de blok-popup na een sleep niet opent.
    this.panEndedClickPending = this.panning();
    this.panning.set(false);
  }

  /** Ctrl+scroll wisselt van zoom-niveau (besluit 19); na week volgt jaren, als die kan. */
  protected onWheel(event: WheelEvent): void {
    if (!event.ctrlKey) return;
    event.preventDefault();
    const levels: readonly HdEphemerisZoomLevel[] = this.yearsAvailable()
      ? [...this.zoomLevels, HD_EPHEMERIS_YEAR_ZOOM]
      : this.zoomLevels;
    const index = levels.indexOf(this.zoom());
    const next = event.deltaY > 0 ? index + 1 : index - 1;
    if (next < 0 || next >= levels.length) return;
    // Terug de zone in: de listener draait erbuiten en setZoom emit naar de
    // parent en schrijft de zoom-setting weg.
    this.ngZone.run(() => this.setZoom(levels[next]));
  }

  /** Hover op een lijn-blok highlight zijn poort-container (besluit 8). */
  protected onBlockHover(item: HdEphemerisRenderItem | null): void {
    this.hoveredGateId.set(item != null && item.kind !== 'gate' ? item.containerId : null);
  }

  protected openBlockPopup(lane: HdEphemerisLaneVm, item: HdEphemerisRenderItem, event: MouseEvent): void {
    if (this.consumePanEndedClick()) return; // de klik sluit een pan-drag af, geen blok-selectie
    event.stopPropagation(); // een blok-klik verplaatst de selectielijn niet
    this.popupService.open(this.blockPopupData(lane, item), { x: event.clientX, y: event.clientY });
  }

  /**
   * Klik op een leeg stuk van de lanes zet de selectielijn op dat tijdstip.
   * `currentTarget` is de SVG zelf: `clientX - rect.left` is de SVG-x (de
   * schaal is 1:1, er is geen viewBox-transformatie).
   */
  protected onLanesTimelineClick(event: MouseEvent): void {
    if (this.consumePanEndedClick()) return;
    const rect = (event.currentTarget as SVGSVGElement).getBoundingClientRect();
    const xPx = event.clientX - rect.left;
    this.selectedTimeMs.set(pxToTime(xPx, this.effectiveRange().fromMs, this.zoomConfig()));
  }

  /** Klik op een ruler-strook zet de selectielijn — de ruler rekent in viewport-coördinaten. */
  protected onRulerTimelineClick(event: MouseEvent): void {
    if (this.consumePanEndedClick()) return;
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const xPx = event.clientX - rect.left;
    this.selectedTimeMs.set(pxToTime(xPx, this.visibleWindow().fromMs, this.zoomConfig()));
  }

  /** Plus-knop op de selectielijn in de header: moment toevoegen op dat tijdstip. */
  protected openEventAdd(event: MouseEvent): void {
    event.stopPropagation(); // geen her-selectie via de ruler-click
    const timeMs = this.selectedTimeMs();
    if (timeMs == null) return;
    this.momentAddRequested.emit({ atUtc: moment.utc(timeMs), withTime: true });
  }

  /** Rechterklik op de selectielijn (of zijn ruler-label) wist de selectie. */
  protected clearSelection(event: MouseEvent): void {
    event.preventDefault(); // geen browser-contextmenu
    event.stopPropagation();
    this.selectedTimeMs.set(null);
  }

  /** Enter/Space op de gefocuste plus-knop vraagt ook een moment aan. */
  protected onAddEventKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    const timeMs = this.selectedTimeMs();
    if (timeMs != null) this.momentAddRequested.emit({ atUtc: moment.utc(timeMs), withTime: true });
  }

  /** `true` wanneer deze click enkel het einde van een pan-drag is (eenmalig). */
  private consumePanEndedClick(): boolean {
    if (!this.panEndedClickPending) return false;
    this.panEndedClickPending = false;
    return true;
  }

  /** Enter/Space op een gefocust blok opent ook de popup (zonder cursor-anker). */
  protected onBlockKeydown(lane: HdEphemerisLaneVm, item: HdEphemerisRenderItem, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.popupService.open(this.blockPopupData(lane, item));
  }

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(`${I18N_PREFIX}.${key}`, params, this.lang());
  }

  private blockPopupData(lane: HdEphemerisLaneVm, item: HdEphemerisRenderItem): HdTransitShiftPopupData {
    const planet = this.planetSegments().find(candidate => candidate.planetKey === lane.planetKey);
    const fromMs = item.fromUtc.valueOf();
    const toMs = item.toUtc.valueOf();
    // Keert de planeet binnen dit blok, dan toont de popup wanneer; ℞ geldt dan niet voor het hele blok.
    const stations = (planet?.stations ?? []).filter(
      station => station.atUtc.valueOf() >= fromMs && station.atUtc.valueOf() < toMs,
    );
    return {
      planetId: lane.planetId,
      planetKey: lane.planetKey,
      gate: item.gate,
      line: item.line,
      fromUtc: item.fromUtc,
      toUtc: item.toUtc,
      retrograde: item.retrograde && stations.length === 0,
      stations,
      // De geladen segmenten van deze planeet — de popup leidt er prev/next af.
      segments: planet?.segments ?? [],
    };
  }

  protected openYearBoxPopup(lane: HdEphemerisLaneVm, box: HdEphemerisYearBoxVm, event: MouseEvent): void {
    if (this.consumePanEndedClick()) return; // de klik sluit een pan-drag af
    event.stopPropagation(); // een blok-klik verplaatst de selectielijn niet
    this.popupService.open(this.yearBoxPopupData(lane, box), { x: event.clientX, y: event.clientY });
  }

  protected onYearBoxKeydown(lane: HdEphemerisLaneVm, box: HdEphemerisYearBoxVm, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.popupService.open(this.yearBoxPopupData(lane, box));
  }

  /**
   * Popup van een poortperiode: van de eerste intrede tot de laatste uittrede,
   * met de stations (de keerpunten van het heen en weer) erin. De periodes gaan
   * als segmenten mee, zodat de info-card de vorige/volgende poort(periode)
   * toont i.p.v. de buurpoort waarin de planeet even terugviel.
   */
  private yearBoxPopupData(lane: HdEphemerisLaneVm, box: HdEphemerisYearBoxVm): HdTransitShiftPopupData {
    const { epoch } = box;
    const fromMs = epoch.fromUtc.valueOf();
    const toMs = epoch.toUtc.valueOf();
    const planet = this.planetSegments().find(candidate => candidate.planetKey === lane.planetKey);
    const epochs = this.gateEpochsByPlanet().get(lane.planetKey) ?? [];
    return {
      planetId: lane.planetId,
      planetKey: lane.planetKey,
      gate: epoch.gate,
      line: null,
      fromUtc: epoch.fromUtc,
      toUtc: epoch.toUtc,
      retrograde: false,
      stations: (planet?.stations ?? []).filter(
        station => station.atUtc.valueOf() >= fromMs && station.atUtc.valueOf() < toMs,
      ),
      segments: epochs.map(candidate => ({
        gate: candidate.gate,
        line: null,
        fromUtc: candidate.fromUtc,
        toUtc: candidate.toUtc,
        retrograde: false,
      })),
    };
  }

  /** Lane van de jaren-weergave: enkel poortperiode-blokken (geen lijnen, stations of lussen). */
  private yearLane(
    key: HdPlanetKey,
    index: number,
    lang: string,
    boxes: readonly HdEphemerisYearBox[],
  ): HdEphemerisLaneVm {
    const planetConfig = getHdPlanetDisplayConfig(key);
    const name = this.planetInfo.planetInfoIn(planetConfig.key, lang).name;
    return {
      planetKey: planetConfig.key,
      planetId: planetConfig.planetId,
      name,
      glyph: planetConfig.astronomiconChar,
      stripeColor: planetConfig.stripeColor,
      gateFill: hexToRgba(planetConfig.stripeColor, GATE_FILL_ALPHA),
      lineFill: hexToRgba(planetConfig.stripeColor, LINE_FILL_ALPHA),
      yPx: index * HD_EPHEMERIS_YEAR_LANE_HEIGHT,
      items: [],
      stations: [],
      loops: [],
      boundaries: [],
      verticalGateLabels: false,
      yearBoxes: boxes.map(box => this.yearBoxVm(name, box)),
      yearAwayFill: hexToRgba(planetConfig.stripeColor, YEAR_AWAY_FILL_ALPHA),
      yearVisitFill: hexToRgba(planetConfig.stripeColor, YEAR_VISIT_FILL_ALPHA),
    };
  }

  private yearBoxVm(planetName: string, box: HdEphemerisYearBox): HdEphemerisYearBoxVm {
    const { epoch } = box;
    const title =
      box.titleTier === 'full'
        ? this.t('yearBoxTitle', { gate: epoch.gate })
        : box.titleTier === 'short'
          ? `${epoch.gate}`
          : null;
    const dates = box.datesTier == null ? null : this.yearBoxDateRange(epoch, YEAR_BOX_DATE_FORMATS[box.datesTier]);
    const summary = this.t('yearBoxTooltip', {
      planet: planetName,
      gate: epoch.gate,
      range: this.yearBoxDateRange(epoch, YEAR_BOX_DATE_FORMATS.full),
    });
    const withVisits =
      epoch.visits.length > 1 ? `${summary} · ${this.t('yearBoxVisits', { count: epoch.visits.length })}` : summary;
    // De keynotes van de poort op een eigen regel onder de periode.
    const keynotes = this.gateKeynotes()(epoch.gate);
    return { ...box, title, dates, tooltip: keynotes.length > 0 ? `${withVisits}\n${keynotes}` : withVisits };
  }

  /** "31/01/2002 – 27/10/2005" (lokale tijd); een (nog) onbekende rand wordt "…". */
  private yearBoxDateRange(epoch: HdEphemerisGateEpoch, format: string): string {
    const from = epoch.startKnown ? epoch.fromUtc.clone().local().format(format) : YEAR_BOX_UNKNOWN_DATE;
    const to = epoch.endKnown ? epoch.toUtc.clone().local().format(format) : YEAR_BOX_UNKNOWN_DATE;
    return `${from} – ${to}`;
  }

  private blockAriaLabel(planetName: string, item: HdEphemerisRenderItem): string {
    const params = {
      planet: planetName,
      gate: item.gate,
      line: item.line,
      from: item.fromUtc.clone().local().format(BLOCK_TIME_FORMAT),
      to: item.toUtc.clone().local().format(BLOCK_TIME_FORMAT),
    };
    return item.line != null ? this.t('blockAria', params) : this.t('blockAriaNoLine', params);
  }

  private async loadSegments(
    target: HdEphemerisRange,
    config: HdEphemerisZoomConfig,
    planets: readonly HdPlanetKey[],
    signature: string,
  ): Promise<void> {
    const seq = ++this.loadSeq;
    const fromUtc = moment.utc(target.fromMs);
    const toUtc = moment.utc(target.toMs);
    const result =
      config.level === HD_EPHEMERIS_YEAR_ZOOM
        ? await this.dataService.getLongRangeSegments(fromUtc, toUtc, planets)
        : await this.dataService.getSegments(fromUtc, toUtc, planets, config.granularity);
    if (seq !== this.loadSeq) return; // verouderd antwoord (zoom/scroll was sneller)
    const previous = this.loadedRange();
    this.planetSegments.set(result);
    this.loadedRange.set(target);
    this.loadedSignature = signature;
    if (previous != null && target.fromMs < previous.fromMs) {
      // Links bijgeladen: de px-oorsprong schuift → scroll compenseren zodat
      // het zichtbare tijdvenster (en daarmee de laad-trigger) stabiel blijft.
      this.shiftScrollBy((previous.fromMs - target.fromMs) * this.zoomConfig().pxPerMs);
    }
    if (this.needsRecenter) {
      if (this.pendingScrollRestore) {
        this.restoreLanesScroll(this.pendingScrollRestore);
        this.pendingScrollRestore = null;
      } else {
        this.centerScrollOnAnchor();
      }
      this.needsRecenter = false;
    }
  }

  private shiftScrollBy(deltaPx: number): void {
    this.applyLanesScrollLeft(Math.max(0, this.scrollLeftPx() + deltaPx));
  }

  /**
   * Zet het anker op het ankerpunt van het niveau (golden ratio: iets meer
   * toekomst dan verleden zichtbaar; jaren: "nu" in het midden). Tijdens de load
   * staat de load-overlay in plaats van de lanes in de DOM, dus bestaat de
   * scroll-container op dit moment vaak nog niet: dan zou enkel het signaal
   * verschuiven en toonde de
   * (nadien opnieuw opgebouwde) container x=0 terwijl de blokken rond het
   * anker getekend worden — een lege lane-strook. Daarom dezelfde
   * "nu + na het render-frame"-toepassing als bij het scroll-herstel.
   */
  private centerScrollOnAnchor(fraction = this.zoomConfig().anchorFraction): void {
    const x =
      timeToPx(this.anchorMs(), this.effectiveRange().fromMs, this.zoomConfig()) -
      this.viewportWidthPx() * fraction;
    const next = Math.max(0, x);
    this.applyLanesScrollLeft(next);
    this.afterRenderFrame(() => this.applyLanesScrollLeft(next));
  }

  /**
   * Herstelt de bewaarde scrollposities na een dock-tab-/view-wissel: direct
   * (voor zover de DOM al bijgewerkt is) én nogmaals na het render-frame —
   * hetzelfde patroon als de `scheduleScrollPositionRestore` van de
   * dock-hosts. Een herstelde positie telt als eigen navigatie: bij een
   * resize blijft zijn midden behouden.
   */
  private restoreLanesScroll(saved: HdTransitsLanesScrollState): void {
    this.applyLanesScroll(saved);
    this.afterRenderFrame(() => this.applyLanesScroll(saved));
    this.userNavigated = true;
  }

  private applyLanesScroll(saved: HdTransitsLanesScrollState): void {
    // Het bewaarde middelpunt komt weer in het midden — onafhankelijk van de
    // nieuwe viewport-breedte (dock ↔ dialog ↔ narrow). Oude staat zonder
    // centerTimeMs valt terug op de bewaarde px-scroll.
    if (saved.centerTimeMs != null) {
      const x =
        timeToPx(saved.centerTimeMs, this.effectiveRange().fromMs, this.zoomConfig()) -
        this.viewportWidthPx() / 2;
      this.applyLanesScrollLeft(Math.max(0, x));
    } else {
      this.applyLanesScrollLeft(saved.lanesScrollLeftPx);
    }
    this.hostElement.nativeElement.scrollTop = saved.panelScrollTopPx;
  }

  private applyLanesScrollLeft(px: number): void {
    this.lastProgrammaticScrollPx = px;
    this.applyScrollPositionPx(px);
    const scrollEl = this.scrollContainer()?.nativeElement;
    if (scrollEl != null) scrollEl.scrollLeft = px;
  }

  /** Voert `apply` nogmaals uit ná het render-frame, wanneer de DOM bijgewerkt is. */
  private afterRenderFrame(apply: () => void): void {
    const view = this.hostElement.nativeElement.ownerDocument.defaultView;
    if (!view) return;
    this.cancelPendingScrollRestore();
    const frameId = view.requestAnimationFrame(() => {
      this.pendingRestoreFrame = null;
      apply();
    });
    this.pendingRestoreFrame = { frameId, view };
  }

  private cancelPendingScrollRestore(): void {
    if (this.pendingRestoreFrame === null) return;
    this.pendingRestoreFrame.view.cancelAnimationFrame(this.pendingRestoreFrame.frameId);
    this.pendingRestoreFrame = null;
  }
}
